import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, MapPin, Clock, Calendar, Tag, ChevronRight } from "lucide-react";
import { Reveal, Overline } from "@/components/public/Reveal";
import CallbackForm from "@/components/public/CallbackForm";
import api, { fileUrl } from "@/lib/api";
import Seo from "@/components/Seo";

const ART_FALLBACK = "https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?w=1400&q=80";

export default function ArticleDetail() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/content/${slug}`).then((r) => setC(r.data)).catch(() => {});
    api.get("/content").then((r) => setRelated(r.data)).catch(() => {});
  }, [slug]);

  const plain = useMemo(() => (c?.body || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim(), [c]);
  const wordCount = plain ? plain.split(" ").length : 0;
  const readMin = Math.max(1, Math.round(wordCount / 200));
  const dateStr = c?.created_at ? new Date(c.created_at).toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" }) : "";
  const rel = related.filter((r) => r.slug !== slug).slice(0, 3);

  if (!c) return <div className="pt-40 pb-40 text-center text-stone">Chargement…</div>;

  const ld = [
    {
      "@context": "https://schema.org",
      "@type": c.type === "faq" ? "FAQPage" : "Article",
      headline: c.seo_title || c.title,
      description: c.seo_description || c.excerpt,
      image: c.cover_image || ART_FALLBACK,
      keywords: (c.keywords || []).join(", "),
      wordCount,
      inLanguage: "fr-FR",
      author: { "@type": "Organization", name: "Acanthe Jardins" },
      publisher: { "@type": "Organization", name: "Acanthe Jardins", logo: { "@type": "ImageObject", url: (typeof window !== "undefined" ? window.location.origin : "") + "/logo-acanthe.png" } },
      datePublished: c.created_at,
      dateModified: c.created_at,
      mainEntityOfPage: typeof window !== "undefined" ? window.location.href : "",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil", item: typeof window !== "undefined" ? window.location.origin : "" },
        { "@type": "ListItem", position: 2, name: "Conseils", item: (typeof window !== "undefined" ? window.location.origin : "") + "/conseils" },
        { "@type": "ListItem", position: 3, name: c.title },
      ],
    },
  ];

  return (
    <div className="bg-cream">
      <Seo title={c.seo_title || c.title} path={`/conseils/${c.slug}`} description={c.seo_description || c.excerpt} image={c.cover_image} type="article" jsonLd={ld} />

      {/* Header */}
      <section className="pt-32 md:pt-40 pb-8">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <nav className="flex items-center gap-1.5 text-xs text-stone mb-6" aria-label="Fil d'ariane">
            <Link to="/" className="hover:text-brand">Accueil</Link><ChevronRight size={13} />
            <Link to="/conseils" className="hover:text-brand">Conseils</Link><ChevronRight size={13} />
            <span className="text-forest font-medium truncate">{c.title}</span>
          </nav>
          {c.ville && <Overline><span className="inline-flex items-center gap-1"><MapPin size={13} />{c.ville} {c.code_postal}</span></Overline>}
          <h1 className="mt-3 font-display text-4xl lg:text-5xl text-forest leading-[1.05]">{c.title}</h1>
          {c.excerpt && <p className="mt-5 text-xl text-stone leading-relaxed font-display">{c.excerpt}</p>}
          <div className="mt-6 flex flex-wrap items-center gap-5 text-sm text-stone">
            {dateStr && <span className="inline-flex items-center gap-1.5"><Calendar size={15} className="text-brand" /> {dateStr}</span>}
            <span className="inline-flex items-center gap-1.5"><Clock size={15} className="text-brand" /> {readMin} min de lecture</span>
            <span className="inline-flex items-center gap-1.5"><Tag size={15} className="text-brand" /> Acanthe Jardins</span>
          </div>
        </div>
      </section>

      {/* Cover */}
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <img src={c.cover_image ? fileUrl(c.cover_image) : ART_FALLBACK} alt={c.title} className="w-full aspect-[16/8] object-cover rounded-[24px] shadow-sm" data-testid="article-cover" />
      </div>

      {/* Body */}
      <article className="max-w-3xl mx-auto px-6 md:px-12 py-14">
        <div className="prose-acanthe" dangerouslySetInnerHTML={{ __html: c.body }} data-testid="article-body" />

        {c.keywords?.length > 0 && (
          <div className="mt-12 pt-8 border-t border-border">
            <p className="text-xs font-bold uppercase tracking-wider text-stone mb-3">Mots-clés</p>
            <div className="flex flex-wrap gap-2" data-testid="article-keywords">
              {c.keywords.map((k) => <span key={k} className="rounded-full bg-sage px-3 py-1 text-xs text-forest font-medium">{k}</span>)}
            </div>
          </div>
        )}

        {/* Internal links CTA */}
        <div className="mt-8 rounded-2xl bg-[#F0EFEA] p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-forest font-medium">Un projet d'aménagement ou d'entretien ? Découvrez nos prestations ou demandez un devis.</p>
          <div className="flex gap-3 shrink-0">
            <Link to="/prestations" className="rounded-full border border-brand px-5 py-2.5 text-sm font-semibold text-brand hover:bg-brand hover:text-cream transition-colors">Nos prestations</Link>
            <Link to="/contact" className="rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-cream hover:bg-forest transition-colors">Devis gratuit</Link>
          </div>
        </div>
      </article>

      {/* Related */}
      {rel.length > 0 && (
        <section className="max-w-5xl mx-auto px-6 md:px-12 pb-4">
          <h2 className="font-display text-3xl text-forest mb-6">À lire également</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {rel.map((r) => (
              <Link key={r.id} to={`/conseils/${r.slug}`} data-testid={`related-${r.slug}`}
                className="group rounded-2xl overflow-hidden bg-white border border-border hover:-translate-y-1 hover:shadow-lg transition-all">
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={r.cover_image ? fileUrl(r.cover_image) : ART_FALLBACK} alt={r.title} className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-700" />
                </div>
                <div className="p-4">
                  <h3 className="font-display text-lg text-forest leading-tight line-clamp-2">{r.title}</h3>
                  <span className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-brand group-hover:gap-2 transition-all">Lire <ArrowRight size={13} /></span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <div className="max-w-2xl mx-auto px-6 py-16">
        <div className="rounded-2xl bg-forest p-8 text-cream text-center">
          <h3 className="font-display text-3xl mb-2">Besoin d'un accompagnement ?</h3>
          <p className="text-cream/70 mb-6">Demandez votre étude gratuite dès aujourd'hui.</p>
          <div className="bg-white rounded-xl p-6 text-left"><CallbackForm compact /></div>
        </div>
      </div>
    </div>
  );
}
